import { useFormContext } from 'react-hook-form';

import { OppvarmingBruk, OppvarmingFormData } from '../types';

export const useOppvarmingFormContext = () => {
  return useFormContext<OppvarmingFormData>();
};

// Fields where selection validation should be triggered. Used as dependency list in react-hook-form register method.
export const oppvarmingSelectionFields = [
  'elektrisk.selected',
  'bioenergi.selected',
  'fjernvarme.selected',
  'varmepumpe.selected',
  'olje.selected',
  'gass.selected',
  'ved.selected',
  'solfanger.selected'
];

const hasOnlyTappevannSelected = (bruk?: OppvarmingBruk) => {
  return bruk?.tappevann && !bruk?.romoppvarming && !bruk?.ukjent && !bruk?.ventilasjonVarmebatteri;
};

export const hasSelectedOppvarmingskilde = (formData?: OppvarmingFormData) => {
  // If heating sources is selected, but only used as "Tappevann" we display a warning
  return (
    (formData?.bioenergi?.selected && hasBioenergiHeatingSource(formData)) ||
    (formData?.elektrisk?.selected && hasElektriskHeatingSource(formData)) ||
    (formData?.gass?.selected && hasGassHeatingSource(formData)) ||
    (formData?.olje?.selected && hasOljeHeatingSource(formData)) ||
    (formData?.varmepumpe?.selected && hasVarmepumpeHeatingSource(formData)) ||
    (formData?.fjernvarme?.selected && !hasOnlyTappevannSelected(formData?.fjernvarme?.value?.bruk)) ||
    (formData?.solfanger?.selected && !hasOnlyTappevannSelected(formData?.solfanger?.value?.bruk)) ||
    formData?.ved?.selected
  );
};

// Check that bioenergi has a heating source that is not only used for "Tappevann"
const hasBioenergiHeatingSource = ({ bioenergi }: OppvarmingFormData) => {
  const kjel = bioenergi?.value?.kjel;
  const kamin = bioenergi?.value?.kamin;
  const kaminMedVannbarenVarme = bioenergi?.value?.kaminMedVannbarenVarme;

  return (
    kamin?.selected ||
    (kjel?.selected && !hasOnlyTappevannSelected(kjel?.value?.bruk)) ||
    (kaminMedVannbarenVarme?.selected && !hasOnlyTappevannSelected(kaminMedVannbarenVarme?.value?.bruk))
  );
};

// Check that gass has a heating source that is not used for "Tappevann"
const hasGassHeatingSource = ({ gass }: OppvarmingFormData) => {
  const kjel = gass?.value?.kjel;
  const kamin = gass?.value?.kamin;

  return kamin?.selected || (kjel?.selected && !hasOnlyTappevannSelected(kjel?.value?.bruk));
};

// Check that olje has a heating source that is not used for "Tappevann"
const hasOljeHeatingSource = ({ olje }: OppvarmingFormData) => {
  const kjel = olje?.value?.kjel;
  const kamin = olje?.value?.kamin;

  return kamin?.selected || (kjel?.selected && !hasOnlyTappevannSelected(kjel?.value?.bruk));
};

// Check that varmepumpe has a heating source that is not used for "Tappevann"
const hasVarmepumpeHeatingSource = ({ varmepumpe }: OppvarmingFormData) => {
  return (
    (!!varmepumpe?.value?.vannbarenVarme && !hasOnlyTappevannSelected(varmepumpe?.value.vannbarenVarme.bruk)) ||
    !!varmepumpe?.value?.punktoppvarming
  );
};

// Check that elektrisk has a heating source that is not used for "Tappevann"
const hasElektriskHeatingSource = ({ elektrisk }: OppvarmingFormData) => {
  return (
    (!!elektrisk?.value?.kjel?.selected && !hasOnlyTappevannSelected(elektrisk?.value?.kjel.value?.bruk)) ||
    !!elektrisk?.value?.gulvvarme?.selected ||
    !!elektrisk?.value?.luftvarme?.selected ||
    !!elektrisk?.value?.panelovn?.selected ||
    !!elektrisk?.value?.takvarme?.selected
  );
};
